import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { DashboardComponentType } from '@/features/dashboard'

export interface Feature<Props = any> {
  name: string
  enabled: boolean
  type?: DashboardComponentType
  access?: string[]
  title: LocaleString
  info?: LocaleString
  component?: string
  titleShort?: LocaleString
  roleAccess?: string[]
  realmAccess?: string[]
  isRoute?: boolean
  props?: Props
  parent?: Feature<any>
  children?: Feature<any>[]
}

export function filterAccess(feature: Feature, realmId: string, featureAccess: string[]): Feature {
  return {
    ...feature,
    children: feature.children
      ?.map((child) => filterAccess(child, realmId, featureAccess))
      .filter((child) => {
        if (!child.enabled) return false
        if (child.realmAccess && realmId && !child.realmAccess.includes(realmId)) return false
        return child.roleAccess?.some((access) => featureAccess.includes(access)) ?? true
      })
  }
}

export function mutateFeature(
  feature: Feature,
  path: string[],
  realmId: string,
  featureAccess: string[],
  mutation: (feature: Feature) => Feature
): Feature {
  if (path.length === 1 && feature.name === path[0])
    return mutation(feature as unknown as Feature) as unknown as Feature
  return {
    ...feature,
    children: feature.children?.map((child) =>
      mutateFeature(child, path.slice(1), realmId, featureAccess, mutation)
    )
  }
}

export function getFeatureFromPath(
  rootFeature: Feature,
  path: string[],
  featurePath?: string[]
): Feature | undefined {
  let current = rootFeature
  const currentFeaturePath = [...(featurePath ?? path)].slice(1)

  const pathLength = path.length
  path = path.slice(1)

  let depth = 1
  while (path.length > 0) {
    const name = path.shift()
    const featureName = currentFeaturePath.shift()
    if (!featureName && name === '*') return undefined

    for (const child of current.children ?? []) {
      if (
        child.name === name ||
        featureName === 'digital-buildings' ||
        (name === '*' && featureName === child.name)
      ) {
        current = child
        depth++
        break
      }
    }
  }

  if (depth !== pathLength) return undefined

  return current
}

export function setParents(feature: Feature, parent?: Feature) {
  feature.parent = parent
  feature.children?.forEach((child) => setParents(child, feature))
}

export function getFeaturePath(feature: Feature): Feature[] {
  if (!feature.parent) return [feature]
  return [...getFeaturePath(feature.parent), feature]
}

export function reconstructFeaturePath(feature: Feature, path: string[]): string[] {
  let current: Feature | undefined = feature

  path.shift()

  while (path.length > 0 && current) {
    const name = path.shift()

    if (!name) break

    current =
      current.children?.find((child) => child.name === name) ??
      findChildByNameBreadth(current, name)
  }

  return [
    'home',
    ...getFeaturePath(current ?? feature)
      .map((f) => f.name)
      .slice(1)
  ]
}

export function findChildByNameBreadth(feature: Feature, name: string): Feature | undefined {
  if (feature.name === name) return feature
  return searchBreadthFirst(feature, (f) => f.name === name)
}

export function searchBreadthFirst(
  feature: Feature,
  predicate: (feature: Feature) => boolean
): Feature | undefined {
  const queue = [feature]
  while (queue.length > 0) {
    const current = queue.shift()!
    if (predicate(current)) return current
    queue.push(...(current.children ?? []))
  }
}
